import { createPinia } from 'pinia';
import { createApp } from 'vue';

import App from './App.vue';
import router from './router';
import { useAuthStore } from './stores/auth';
import { disconnect } from './composables/useWebSocket';
import { setUnauthorisedHandler } from './utils/api';
import { healStaleBuild } from './utils/heal';

import './assets/styles/main.scss';

/*
 * Self-heal after a deploy.
 *
 * Views load as hashed chunks, and a deploy replaces the whole set: a tab
 * that has been open since before the deploy holds an index that asks for
 * files that no longer exist. Every click that opened a view then waited on
 * a download that could only fail - the game felt laggy, and pages came up
 * with their styles missing. Vite announces exactly this failure, and the
 * only honest cure is the one the player would eventually reach for anyway:
 * reload, which fetches the current index and the chunks that go with it.
 */
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  healStaleBuild();
});

// Drop the cache-busting marker a self-heal appended, so it never lingers
// in the address bar or gets bookmarked.
if (window.location.search.includes('fresh=')) {
  const url = new URL(window.location.href);
  url.searchParams.delete('fresh');
  window.history.replaceState(history.state, '', url.pathname + url.search + url.hash);
}

const app = createApp(App);

app.use(createPinia());
app.use(router);

// One place decides what happens when the server stops trusting our token:
// drop the socket, clear the session, and bounce to the login screen.
setUnauthorisedHandler(() => {
  disconnect();
  useAuthStore().clear();
  if (router.currentRoute.value.name !== 'login') void router.replace({ name: 'login' });
});

app.mount('#app');
